import { useEffect, useState } from 'react'
import { Check, ListChecks } from 'lucide-react'
import type { ListItem, ListSpec } from '@/kinds/list/schema'
import { cx } from '@/components/ui'
import { FileSharePage, type FileShareInfo } from './files/SharePage'

/**
 * The standalone share page at /s/<secret>. No device, no sync, no shell: the
 * secret is the bearer for GET /api/share and POST /api/share/actions, and the
 * page renders exactly what the grant names. A shared list is tickable and
 * takes new items; everything else is read-only.
 */
type Item = {
  kind: string
  id: string
  name: string
  spec: Record<string, unknown>
  status: Record<string, unknown>
  write: boolean
}
type Payload = {
  grant: {
    name: string
    expires_at?: string
    one_time: boolean
    capture?: boolean
    purpose?: string
    uses_left?: number
    intro?: string
    buttons?: string[]
    fields?: ('details' | 'when' | 'link')[]
    paused?: boolean
    owner?: string
    /** the owner's latest note to this link's holder */
    reply?: string
    reply_at?: string
  }
  items: Item[]
  /** what came through this link lately, and what became of it */
  sent?: { title: string; at: string; state: 'sent' | 'seen' | 'done' }[]
  /** a file share (docs/areas/files.md): its own page */
  files?: FileShareInfo
}

export const SharedView = ({ secret }: { secret: string }) => {
  const [data, setData] = useState<Payload | null>(null)
  const [error, setError] = useState<string | null>(null)
  const headers = { Authorization: `Bearer ${secret}`, 'Content-Type': 'application/json' }
  const [loads, setLoads] = useState(0)
  useEffect(() => {
    let cancelled = false
    fetch('/api/share', { headers: { Authorization: `Bearer ${secret}` } })
      .then(async (res) => {
        if (cancelled) return
        if (!res.ok) {
          const body: unknown = await res.json().catch(() => ({}))
          setError(linkTrouble(res.status, errorText(body)))
          return
        }
        setData((await res.json()) as Payload)
      })
      .catch(() => {
        if (!cancelled) setError('You seem to be offline.')
      })
    return () => {
      cancelled = true
    }
  }, [secret, loads])
  const act = async (id: string, action: string, args: unknown) => {
    const res = await fetch('/api/share/actions', {
      method: 'POST',
      headers,
      body: JSON.stringify({ id, action, args }),
    })
    if (!res.ok) {
      const body: unknown = await res.json().catch(() => ({}))
      setError(errorText(body) ?? 'That did not go through.')
      return
    }
    const out = (await res.json()) as {
      resource: { spec: Record<string, unknown>; status: Record<string, unknown> }
    }
    setData(
      (d) =>
        d && {
          ...d,
          items: d.items.map((it) =>
            it.id === id ? { ...it, spec: out.resource.spec, status: out.resource.status } : it,
          ),
        },
    )
  }
  return (
    <div className="min-h-dvh bg-ink text-fg">
      <div className="max-w-lg mx-auto px-4 pt-[calc(1rem+env(safe-area-inset-top))] pb-[calc(2rem+env(safe-area-inset-bottom))]">
        <header className="flex items-center justify-between mb-5">
          <span className="text-sm text-muted">Shared from AlfieOS</span>
          {data?.grant.one_time && (
            <span className="text-[13px] font-semibold text-faint">one-time link</span>
          )}
        </header>
        {error && (
          <div className="mb-4" data-testid="share-error">
            <h1 className="text-2xl font-semibold tracking-tight mb-2">
              {data ? 'That did not go through' : 'This link does not open'}
            </h1>
            <p className="text-[15px] text-muted">{error}</p>
          </div>
        )}
        {!data && !error && <p className="text-sm text-faint">Loading…</p>}
        {data?.grant.capture && data.items.length === 0 && data.grant.reply && (
          <aside
            className="mb-6 rounded-2xl border border-line bg-raised px-4 py-3"
            data-testid="capture-reply"
          >
            <p className="text-[13px] font-semibold text-muted">{data.grant.owner || 'A note'} says</p>
            <p className="text-[15px] whitespace-pre-line mt-0.5">{data.grant.reply}</p>
          </aside>
        )}
        {data?.grant.capture && data.items.length === 0 && (
          <CaptureForm
            secret={secret}
            grant={data.grant}
            sent={data.sent ?? []}
            onSent={() => setLoads((n) => n + 1)}
          />
        )}
        {data?.files && <FileSharePage secret={secret} initial={data.files} />}
        {data && !data.files && !data.grant.capture && data.items.length === 0 && (
          <p className="text-sm text-faint">Nothing is shared on this link any more.</p>
        )}
        {data?.items.map((it) =>
          it.kind === 'List' ? (
            <SharedList key={it.id} item={it} onAct={(action, args) => act(it.id, action, args)} />
          ) : (
            <SharedRead key={it.id} item={it} />
          ),
        )}
        {/* the link's name is the owner's own note to himself ("only you see this"): never shown here */}
        {data?.grant.expires_at && (
          <p className="mt-10 text-center text-[11px] text-faint">
            This link works until {new Date(data.grant.expires_at).toLocaleDateString()}.
          </p>
        )}
      </div>
    </div>
  )
}

const SharedList = ({
  item,
  onAct,
}: {
  item: Item
  onAct: (action: string, args: unknown) => Promise<void>
}) => {
  const spec = item.spec as unknown as ListSpec
  const [draft, setDraft] = useState('')
  const open = spec.items.filter((i) => !i.done)
  const done = spec.items.filter((i) => i.done)
  const add = async () => {
    const texts = draft
      .split('\n')
      .map((t) => t.trim())
      .filter(Boolean)
    if (!texts.length) return
    setDraft('')
    await onAct('add_items', { items: texts.map((text) => ({ text })) })
  }
  return (
    <section className="mb-8" data-testid="share-list">
      <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight mb-4">
        <ListChecks size={22} className="text-muted" aria-hidden /> {spec.title}
      </h1>
      {spec.items.length === 0 && <p className="text-sm text-faint mb-3">Nothing on it yet.</p>}
      <ul className="group-list">
        {open.map((it) => (
          <ShareRow key={it.id} it={it} write={item.write} onAct={onAct} />
        ))}
      </ul>
      {done.length > 0 && (
        <>
          <h2 className="text-[13px] font-semibold text-muted mt-5 mb-1.5">Ticked</h2>
          <ul className="group-list">
            {done.map((it) => (
              <ShareRow key={it.id} it={it} write={item.write} onAct={onAct} />
            ))}
          </ul>
        </>
      )}
      {item.write && (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            void add()
          }}
          className="mt-4 flex gap-2 rounded-full bg-panel border border-line p-1.5"
        >
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Add an item…"
            className="bare-input flex-1 min-w-0 px-3"
            data-testid="share-add-input"
          />
          <button
            type="submit"
            disabled={!draft.trim()}
            className="rounded-full bg-accent text-on-accent px-4 min-h-10 text-sm font-medium disabled:opacity-40"
            data-testid="share-add-submit"
          >
            Add
          </button>
        </form>
      )}
    </section>
  )
}

const ShareRow = ({
  it,
  write,
  onAct,
}: {
  it: ListItem
  write: boolean
  onAct: (action: string, args: unknown) => Promise<void>
}) => (
  <li
    className={cx('flex items-center gap-1 pl-1 pr-3', it.done && 'opacity-60')}
    data-share-item={it.id}
    data-done={it.done ? 'yes' : 'no'}
  >
    <button
      type="button"
      disabled={!write}
      onClick={() => void onAct(it.done ? 'untick' : 'tick', { id: it.id })}
      className="h-12 w-12 shrink-0 rounded-full flex items-center justify-center active:scale-95 transition-transform disabled:opacity-50"
      aria-label={it.done ? `Untick ${it.text}` : `Tick ${it.text}`}
    >
      <span
        className={cx(
          'h-6 w-6 rounded-full border-2 flex items-center justify-center',
          it.done ? 'bg-accent border-accent text-on-accent' : 'border-line-strong',
        )}
      >
        {it.done && <Check size={14} strokeWidth={3} aria-hidden />}
      </span>
    </button>
    <span
      className={cx('flex-1 py-3 text-[15px] leading-snug', it.done ? 'line-through text-muted' : 'text-fg')}
    >
      {it.text}
    </span>
    {it.added_by && (
      <span className="text-[11px] text-faint">
        {it.added_by.charAt(0).toUpperCase() + it.added_by.slice(1)}
      </span>
    )}
  </li>
)

const STATE_WORDS = { sent: 'Sent', seen: 'has it', done: 'Done' } as const

/**
 * A capture page: the owner's question, a line under it, a few one-tap
 * starters, one box (and any extra fields the owner turned on), Send; then
 * what was sent lately and what became of it. What they write lands in the
 * inbox, where it waits for the owner.
 */
const CaptureForm = ({
  secret,
  grant,
  sent,
  onSent,
}: {
  secret: string
  grant: Payload['grant']
  sent: NonNullable<Payload['sent']>
  onSent: () => void
}) => {
  const owner = grant.owner || 'Alfie'
  const asks = (f: 'details' | 'when' | 'link') => grant.fields?.includes(f) ?? false
  const [text, setText] = useState('')
  const [details, setDetails] = useState('')
  const [day, setDay] = useState('')
  const [time, setTime] = useState('')
  const [link, setLink] = useState('')
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle')
  const [error, setError] = useState<string | null>(null)
  const oneTime = grant.one_time
  const send = async () => {
    if (!text.trim()) return
    setState('sending')
    setError(null)
    try {
      const res = await fetch('/api/capture', {
        method: 'POST',
        headers: { Authorization: `Bearer ${secret}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: text.trim(),
          ...(details.trim() ? { details: details.trim() } : {}),
          ...(day ? { when: time ? `${day}T${time}` : day } : {}),
          ...(link.trim() ? { link: link.trim() } : {}),
        }),
      })
      if (!res.ok) {
        const body: unknown = await res.json().catch(() => ({}))
        setError(errorText(body) ?? 'That did not go through.')
        setState('error')
        return
      }
      setState('sent')
      setText('')
      setDetails('')
      setDay('')
      setTime('')
      setLink('')
      onSent()
    } catch {
      setError('You seem to be offline.')
      setState('error')
    }
  }
  const history = sent.length > 0 && (
    <section className="mt-8" data-testid="capture-history">
      <h2 className="text-[13px] font-semibold text-muted mb-2">What you’ve sent</h2>
      <ul className="rounded-2xl border border-line divide-y divide-line overflow-hidden">
        {sent.map((x, i) => (
          <li key={i} className="flex items-center gap-3 px-3.5 min-h-12 py-2 text-sm">
            <span className="flex-1 min-w-0 truncate">{x.title}</span>
            <span
              className={
                x.state === 'done'
                  ? 'text-[12px] font-medium text-accent-text'
                  : x.state === 'seen'
                    ? 'text-[12px] text-fg-2'
                    : 'text-[12px] text-faint'
              }
              data-testid="capture-history-state"
            >
              {x.state === 'seen' ? `${owner} has it` : STATE_WORDS[x.state]}
            </span>
          </li>
        ))}
      </ul>
    </section>
  )
  if (grant.paused)
    return (
      <section className="mb-8" data-testid="capture-paused">
        <h1 className="text-2xl font-semibold tracking-tight mb-2">
          {grant.purpose || `Send ${owner} a note`}
        </h1>
        <p className="text-sm text-muted">This link is paused for now. Try again another time.</p>
        {history}
      </section>
    )
  if (state === 'sent')
    return (
      <section className="mb-8" data-testid="capture-sent">
        <h1 className="text-2xl font-semibold tracking-tight mb-2">Sent</h1>
        <p className="text-sm text-muted">
          {oneTime ? 'Thanks. This link has done its job.' : `Thanks. ${owner} will see it.`}
        </p>
        {!oneTime && (
          <button
            type="button"
            className="mt-4 rounded-full bg-raised border border-line px-4 min-h-10 text-sm"
            onClick={() => setState('idle')}
          >
            Send another
          </button>
        )}
        {history}
      </section>
    )
  return (
    <section className="mb-8" data-testid="capture-page">
      <h1 className="text-2xl font-semibold tracking-tight">{grant.purpose || `Send ${owner} a note`}</h1>
      {grant.intro && <p className="text-[15px] text-muted mt-1">{grant.intro}</p>}
      <form
        className="mt-4"
        onSubmit={(e) => {
          e.preventDefault()
          void send()
        }}
      >
        {(grant.buttons?.length ?? 0) > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3" data-testid="capture-buttons">
            {grant.buttons!.map((b) => (
              <button
                key={b}
                type="button"
                className="rounded-full bg-raised border border-line px-3.5 min-h-9 text-sm"
                onClick={() => setText((t) => (t.trim() ? t : `${b.replace(/…$/, '')} `))}
              >
                {b}
              </button>
            ))}
          </div>
        )}
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={asks('details') ? 2 : 5}
          placeholder="Type it here…"
          className="w-full rounded-2xl"
          autoFocus
          data-testid="capture-page-text"
        />
        {asks('details') && (
          <label className="block mt-3">
            <span className="block text-[13px] font-semibold text-muted mb-1">Details</span>
            <textarea
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              rows={4}
              placeholder="Anything else worth knowing"
              className="w-full rounded-2xl"
              data-testid="capture-page-details"
            />
          </label>
        )}
        {asks('when') && (
          <div className="mt-3 grid grid-cols-[1fr_auto] gap-3">
            <label>
              <span className="block text-[13px] font-semibold text-muted mb-1">When</span>
              <input
                type="date"
                value={day}
                onChange={(e) => setDay(e.target.value)}
                className="w-full"
                data-testid="capture-page-when"
              />
            </label>
            <label>
              <span className="block text-[13px] font-semibold text-muted mb-1">Time</span>
              <input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                className="w-28"
                disabled={!day}
              />
            </label>
          </div>
        )}
        {asks('link') && (
          <label className="block mt-3">
            <span className="block text-[13px] font-semibold text-muted mb-1">Link</span>
            <input
              type="url"
              inputMode="url"
              value={link}
              onChange={(e) => setLink(e.target.value)}
              placeholder="https://"
              className="w-full"
              data-testid="capture-page-link"
            />
          </label>
        )}
        {error && <p className="text-sm text-rose-300 mt-2">{error}</p>}
        <div className="mt-3 flex justify-end">
          <button
            type="submit"
            disabled={!text.trim() || state === 'sending'}
            className="rounded-full bg-accent text-on-accent px-5 min-h-11 text-sm font-medium disabled:opacity-40"
            data-testid="capture-page-send"
          >
            {state === 'sending' ? 'Sending…' : 'Send'}
          </button>
        </div>
      </form>
      {history}
    </section>
  )
}

/**
 * Why a link would not open, in a sentence: the server's short reasons
 * ("revoked", "unauthorized") are for logs, not for the person holding the link.
 */
export const linkTrouble = (status: number, reason: string | undefined): string => {
  const r = (reason ?? '').toLowerCase()
  if (status === 429 || r.includes('too many'))
    return 'Too many tries from here just now. Wait a minute and open it again.'
  if (r.includes('revoked')) return 'It has been revoked by the person who shared it. Ask them for a new one.'
  if (r.includes('expired')) return 'It has expired. Ask the person who shared it for a new one.'
  if (r.includes('used up'))
    return 'It has been used as many times as it allowed. Ask for a new one if you need it.'
  if (status === 401 || status === 404)
    return 'It may have been copied only in part, or it no longer exists. Ask the person who shared it for a new one.'
  return `The server could not open it just now (${status}). Try again in a moment.`
}

/** The server writes errors as {"error": "…"} or {"error": {"message": "…"}}; take either. */
const errorText = (body: unknown): string | undefined => {
  const e = (body as { error?: unknown })?.error
  if (typeof e === 'string') return e
  if (e && typeof e === 'object' && typeof (e as { message?: unknown }).message === 'string')
    return (e as { message: string }).message
  return undefined
}

const SharedRead = ({ item }: { item: Item }) => {
  const s = item.spec as { title?: string; text?: string; notes?: string; outcome?: string }
  const body = s.text ?? s.notes ?? s.outcome ?? ''
  return (
    <section className="mb-8" data-testid="share-read">
      <div className="text-[13px] font-semibold text-muted mb-1">{item.kind}</div>
      <h1 className="text-2xl font-semibold tracking-tight mb-3">{item.name || s.title}</h1>
      {body ? (
        <p className="text-[15px] leading-7 whitespace-pre-wrap">{body}</p>
      ) : (
        <p className="text-sm text-faint">Nothing written here yet.</p>
      )}
    </section>
  )
}
